import React, { useCallback, useEffect, useMemo } from 'react';
import type { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import { store, useAppDispatch } from 'Libs/redux/store';
import { trackEventWithLocation } from 'Services/trackingService';
import { useActionDialogConfirmation } from 'Hooks/useActionDialogConfirmation/useActionDialogConfirmation';
import SPageHeader, { IconPropsType } from 'Components/structural/SPageHeader/SPageHeader';
import type { InferPushQueueItem } from 'Libs/queue/redux/utils';
import UTextLink from 'Components/unit/UTextLink/UTextLink';

import { actions, useModuleSelector } from 'Pages/Courses/redux';
import {
  getHandleDeleteMk,
  getHandleInsertMk,
  getHandleInsertChapterMk,
  getHandleReorderMk,
  getHandleUpdateMk,
  HandleInsertMkParam,
  HandleMkDeleteParams,
  HandleReorderMk,
  MkQueueHandler,
} from 'Pages/Courses/utils/edition-handlers';
import MicroKnowledgeList from 'Pages/Courses/Edition/components/MicroKnowledgeList/MicroKnowledgeList';
import styles from 'Pages/Courses/Edition/views/Microknowledges/Microknowledges.style';
import type { CreateUpdateCommandData } from 'Pages/Courses/utils/commands';
import { MicroKnowledgeChapter, MicroKnowledgeItem } from 'Pages/Courses/utils/mk-mapped-linked-list';

export type MicroknowledgesProps = {
  push: (item: InferPushQueueItem<MkQueueHandler>) => void;
};

export type PublicationModalState = {
  isOpened: boolean;
  isPublished: boolean;
};

type HandleChapterCollapseParams = { id: string };

export const Microknowledges: FunctionComponent<MicroknowledgesProps> = ({ push }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const course = useModuleSelector((state) => state.edition.course);
  const mkList = useModuleSelector((state) => state.edition.microKnowledges);

  const { dialog: chapterDeleteConfirmationDialog, askForConfirmation: askConfirmationForChapterDelete } =
    useActionDialogConfirmation({
      title: t('courses:modal_course_chapter_deletion.title'),
      description: t('courses:modal_course_chapter_deletion.description'),
      primaryActionLabel: t('courses:modal_course_chapter_deletion.action'),
      primaryActionButtonType: 'destructive',
      secondaryActionLabel: t('courses:modal_course_chapter_deletion.secondary_action'),
    });

  const {
    dialog: imageConversionConfirmationDialog,
    askForConfirmation: askConfirmationForImageFromChapterTypeConversion,
  } = useActionDialogConfirmation({
    title: t('courses:modal_course_image_conversion.title'),
    description: t('courses:modal_course_image_conversion.description'),
    primaryActionLabel: t('courses:modal_course_image_conversion.action'),
    primaryActionButtonType: 'destructive',
    secondaryActionLabel: t('courses:modal_course_image_conversion.secondary_action'),
  });

  const {
    dialog: videoConversionConfirmationDialog,
    askForConfirmation: askConfirmationForVideoFromChapterTypeConversion,
  } = useActionDialogConfirmation({
    title: t('courses:modal_course_video_conversion.title'),
    description: t('courses:modal_course_video_conversion.description'),
    primaryActionLabel: t('courses:modal_course_video_conversion.action'),
    primaryActionButtonType: 'destructive',
    secondaryActionLabel: t('courses:modal_course_video_conversion.secondary_action'),
  });

  const [baseHandleDeleteMk, baseHandleUpdateMk, baseHandleInsertMk, baseHandleInsertChapterMk, baseHandleReorderMk] =
    useMemo(
      () => [
        getHandleDeleteMk({ store, enqueue: push }),
        getHandleUpdateMk({ store, enqueue: push }),
        getHandleInsertMk({ store, enqueue: push }),
        getHandleInsertChapterMk({ store, enqueue: push }),
        getHandleReorderMk({ store, enqueue: push }),
      ],
      [push],
    );

  const baseHandleDeleteMkWithTracking = useCallback(
    (params: HandleMkDeleteParams) => {
      baseHandleDeleteMk(params);
      trackEventWithLocation('delete', { context: 'micro-knowledge' });
    },
    [baseHandleDeleteMk],
  );

  const baseHandleUpdateMkWithTracking = useCallback(
    (params: CreateUpdateCommandData) => {
      baseHandleUpdateMk(params);
      if (mkList.items[params.id].type !== params.type) {
        trackEventWithLocation('update', {
          context: `micro-knowledge-${params.type}`,
        });
      }
    },
    [baseHandleUpdateMk, mkList.items],
  );

  const handleInsertChapterMkWithTracking = useCallback(
    (params: HandleInsertMkParam) => {
      baseHandleInsertChapterMk(params);
      trackEventWithLocation('create', { context: 'micro-knowledge' });
    },
    [baseHandleInsertChapterMk],
  );

  const handleInsertMkWithTracking = useCallback(
    (params: HandleInsertMkParam) => {
      const isCollapsedChapter =
        params.targetId &&
        mkList.items[params.targetId]?.type === 'chapter' &&
        (mkList.items[params.targetId] as MicroKnowledgeChapter)?.isCollapsed;

      isCollapsedChapter ? baseHandleInsertChapterMk(params) : baseHandleInsertMk(params);

      trackEventWithLocation('create', { context: 'micro-knowledge' });
    },
    [baseHandleInsertMk, baseHandleInsertChapterMk, mkList.items],
  );

  const handleUpdateMk = useCallback(
    (params: CreateUpdateCommandData) => {
      if (mkList.items[params.id].type === 'chapter' && params.type === 'image') {
        askConfirmationForImageFromChapterTypeConversion(() => baseHandleUpdateMkWithTracking(params));
        return;
      }
      if (mkList.items[params.id].type === 'chapter' && params.type === 'video') {
        askConfirmationForVideoFromChapterTypeConversion(() => baseHandleUpdateMkWithTracking(params));
        return;
      }

      baseHandleUpdateMkWithTracking(params);
    },
    [
      askConfirmationForImageFromChapterTypeConversion,
      baseHandleUpdateMkWithTracking,
      askConfirmationForVideoFromChapterTypeConversion,
      mkList.items,
    ],
  );

  const handleDeleteMk = useCallback(
    (params: HandleMkDeleteParams) => {
      if (mkList.items[params.id].type === 'chapter') {
        askConfirmationForChapterDelete(() => baseHandleDeleteMkWithTracking(params));
        return;
      }
      baseHandleDeleteMkWithTracking(params);
    },
    [askConfirmationForChapterDelete, baseHandleDeleteMkWithTracking, mkList.items],
  );

  const handleChapterCollapse = useCallback(
    (params: HandleChapterCollapseParams) => dispatch(actions.toggleChapterCollapse(params)),
    [dispatch],
  );

  // TODO: implement after V0
  const handleDuplicate = useCallback(() => {}, []);

  const handleReorder = useCallback(
    (args: HandleReorderMk) => {
      baseHandleReorderMk(args);
      trackEventWithLocation('reorder', { context: 'micro-knowledge' });
    },
    [baseHandleReorderMk],
  );

  const handleInsertAddNewChapter = useCallback(() => {
    const lastMk: MicroKnowledgeItem | undefined = Object.values(mkList.items).find((mk) => mk.nextId === null);
    baseHandleInsertChapterMk({ targetId: lastMk?.id || null });
  }, [baseHandleInsertChapterMk, mkList.items]);

  // Insert a head item if the course is empty
  useEffect(() => {
    if (course?.aiGenerationStatus !== 'MANUAL') return;
    if (Object.values(mkList.items).length > 0) return;
    handleInsertChapterMkWithTracking({ targetId: null });
  }, [course, mkList]);

  const pageHeaderIconProps = useMemo(
    (): IconPropsType => ({
      iconName: 'plus',
      iconType: 'accentuated',
      onIconClick: handleInsertAddNewChapter,
    }),
    [handleInsertAddNewChapter],
  );

  return (
    <>
      <div style={styles.wrapper}>
        <div style={styles.list}>
          <SPageHeader
            title={t('courses:course_edition.title')}
            iconProps={pageHeaderIconProps}
            style={styles.header}
          />
          {mkList?.headId && (
            <>
              <MicroKnowledgeList
                microKnowledgeMap={mkList}
                onDelete={handleDeleteMk}
                onInsert={handleInsertMkWithTracking}
                onToggleCollapse={handleChapterCollapse}
                onChange={handleUpdateMk}
                onDuplicate={handleDuplicate}
                onReorder={handleReorder}
              />
              <div style={styles.insertButtonWrapper}>
                <UTextLink
                  text={t('courses:ai_plan_validation.add_knowledge')}
                  leftIcon="plus"
                  iconSize="medium"
                  onClick={handleInsertAddNewChapter}
                />
              </div>
            </>
          )}
        </div>
      </div>
      {chapterDeleteConfirmationDialog}
      {imageConversionConfirmationDialog}
      {videoConversionConfirmationDialog}
    </>
  );
};

export default Microknowledges;
